import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { api, API, publicFileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatRp, formatDateID, formatDateTimeID, statusLabel, statusColor, todayISO, sourceLabel, categoryLabel } from "@/lib/format";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { TrendingUp, Wallet, FileText, CheckCircle2, XCircle, Plus, Upload, Download, History, Users, BookOpen, Image as ImageIcon, Settings as SettingsIcon, FileDown, HeartHandshake, AlertCircle, QrCode } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [reports, setReports] = useState([]);
  const [direct, setDirect] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [reportFilter, setReportFilter] = useState("diajukan");
  const [pledges, setPledges] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);

  const loadAll = async () => {
    try {
      const [d, r, dr, e, n, c, u, p, s, pl, st] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/reports", { params: { status: reportFilter === "all" ? undefined : reportFilter } }),
        api.get("/admin/direct-receipts"),
        api.get("/admin/expenses"),
        api.get("/admin/needs"),
        api.get("/admin/classes"),
        api.get("/admin/users"),
        api.get("/admin/progress-posts"),
        api.get("/admin/settings"),
        api.get("/admin/alumni-pledges"),
        api.get("/admin/setup-status"),
      ]);
      setDash(d.data); setReports(r.data); setDirect(dr.data); setExpenses(e.data);
      setNeeds(n.data); setClasses(c.data); setUsers(u.data); setPosts(p.data); setSettings(s.data);
      setPledges(pl.data); setSetupStatus(st.data);
    } catch (e) { toast.error("Gagal memuat data"); }
  };
  const loadAudit = async () => {
    try { const { data } = await api.get("/admin/audit-log"); setAuditLog(data); } catch {}
  };

  useEffect(() => { loadAll(); loadAudit(); /* eslint-disable-next-line */ }, [reportFilter]);

  if (user && user.role !== "admin") return <Navigate to="/infaq" replace />;
  if (user && user.must_change_password) return <Navigate to="/infaq/ganti-sandi" replace />;
  if (!dash) return <div className="min-h-screen flex items-center justify-center">Memuat...</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header logoFileId={settings?.logo_file_id} siteName="Dashboard Bendahara" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Setup checklist banner */}
        {setupStatus && !setupStatus.complete && (
          <Card className="p-4 mb-5 border-amber-300 bg-amber-50" data-testid="setup-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 shrink-0 mt-1" size={18} />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  Konfigurasi awal belum lengkap ({setupStatus.done}/{setupStatus.total})
                </div>
                <ul className="mt-2 text-sm text-amber-900 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                  {setupStatus.checks.map((c) => (
                    <li key={c.key} className="flex items-center gap-2">
                      {c.done ? <CheckCircle2 size={14} className="text-emerald-600" /> : <span className="w-3 h-3 rounded-full border border-amber-500" />}
                      <span className={c.done ? "line-through opacity-60" : ""}>{c.label}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-800 mt-2">Lengkapi di tab <b>Pengaturan</b> & <b>Kelas &amp; PJ</b> agar laporan publik dan email mingguan berjalan optimal.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="admin-stats-grid">
          <StatCard color="text-emerald-700" icon={<TrendingUp size={16} />} label="Penerimaan" value={formatRp(dash.total_received)} testid="stat-received" />
          <StatCard color="text-amber-700" icon={<FileText size={16} />} label="Belum konfirmasi" value={formatRp(dash.pending_class)} testid="stat-pending" />
          <StatCard color="text-red-700" icon={<FileText size={16} />} label="Pengeluaran" value={formatRp(dash.expenses)} testid="stat-expenses" />
          <StatCard color="text-[#C59B27]" icon={<Wallet size={16} />} label="Saldo" value={formatRp(dash.balance)} testid="stat-balance" />
          <StatCard color="text-[#1B4D3E]" icon={<TrendingUp size={16} />} label="Target" value={`${dash.progress_pct}%`} testid="stat-progress" />
        </div>

        <Tabs defaultValue="konfirmasi" className="w-full">
          <TabsList className="flex flex-wrap h-auto bg-white border border-[#E2E8F0] gap-1 p-1">
            <TabsTrigger value="konfirmasi" data-testid="tab-konfirmasi">Konfirmasi</TabsTrigger>
            <TabsTrigger value="alumni" data-testid="tab-alumni">
              Pledge Alumni
              {pledges.filter((p) => p.status === "pending").length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center bg-amber-500 text-white rounded-full text-xs w-5 h-5">
                  {pledges.filter((p) => p.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="langsung" data-testid="tab-langsung">Penerimaan Langsung</TabsTrigger>
            <TabsTrigger value="pengeluaran" data-testid="tab-pengeluaran">Pengeluaran</TabsTrigger>
            <TabsTrigger value="kebutuhan" data-testid="tab-kebutuhan-admin">Kebutuhan</TabsTrigger>
            <TabsTrigger value="kelas" data-testid="tab-kelas">Kelas & PJ</TabsTrigger>
            <TabsTrigger value="perkembangan" data-testid="tab-perkembangan-admin">Perkembangan</TabsTrigger>
            <TabsTrigger value="pengaturan" data-testid="tab-pengaturan">Pengaturan</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="konfirmasi" className="mt-5">
            <ConfirmationTab reports={reports} filter={reportFilter} setFilter={setReportFilter}
              selected={selectedReports} setSelected={setSelectedReports} reload={loadAll} />
          </TabsContent>
          <TabsContent value="alumni" className="mt-5"><AlumniPledgeTab items={pledges} reload={loadAll} /></TabsContent>
          <TabsContent value="langsung" className="mt-5"><DirectReceiptTab items={direct} reload={loadAll} /></TabsContent>
          <TabsContent value="pengeluaran" className="mt-5"><ExpenseTab items={expenses} needs={needs} reload={loadAll} balance={dash.balance} /></TabsContent>
          <TabsContent value="kebutuhan" className="mt-5"><NeedsTab items={needs} reload={loadAll} /></TabsContent>
          <TabsContent value="kelas" className="mt-5"><ClassesTab classes={classes} users={users} reload={loadAll} /></TabsContent>
          <TabsContent value="perkembangan" className="mt-5"><PostsTab items={posts} reload={loadAll} /></TabsContent>
          <TabsContent value="pengaturan" className="mt-5"><SettingsTab settings={settings} reload={loadAll} /></TabsContent>
          <TabsContent value="audit" className="mt-5"><AuditTab items={auditLog} reload={loadAudit} /></TabsContent>
        </Tabs>

        <div className="mt-10 flex flex-wrap gap-3">
          <PdfReportButton />
          <ExportButton kind="all" label="Ekspor Semua (CSV)" />
          <ExportButton kind="reports" label="Ekspor Laporan Kelas" />
          <ExportButton kind="direct" label="Ekspor Penerimaan Langsung" />
          <ExportButton kind="expenses" label="Ekspor Pengeluaran" />
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, color, testid }) {
  return (
    <Card className="p-4 border-[#E2E8F0]" data-testid={testid}>
      <div className="text-xs text-[#64748B] uppercase tracking-wider flex items-center gap-1">{icon} {label}</div>
      <div className={`text-xl sm:text-2xl font-bold ${color} mt-1`}>{value}</div>
    </Card>
  );
}

function PdfReportButton() {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/report/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laporan_Infak_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan PDF diunduh");
    } catch (e) {
      toast.error("Gagal membuat PDF");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      onClick={download}
      disabled={loading}
      className="bg-[#1B4D3E] hover:bg-[#143E32] text-white"
      data-testid="download-pdf-report-btn"
    >
      <FileDown size={14} className="mr-2" /> {loading ? "Membuat PDF..." : "Laporan PDF Resmi"}
    </Button>
  );
}

function ExportButton({ kind, label }) {
  const download = async () => {
    try {
      const res = await api.get(`/admin/export/csv`, { params: { kind }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `infak_${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal ekspor"); }
  };
  return <Button variant="outline" onClick={download} data-testid={`export-${kind}-btn`}><Download size={14} className="mr-1" /> {label}</Button>;
}

function ConfirmationTab({ reports, filter, setFilter, selected, setSelected, reload }) {
  const [returning, setReturning] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState("");

  const toggle = (id) => {
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  const selectedTotal = reports.filter((r) => selected.includes(r.id)).reduce((s, r) => s + r.amount, 0);

  const confirmSelected = async () => {
    if (selected.length === 0) return toast.error("Pilih laporan");
    try {
      const { data } = await api.post("/admin/reports/confirm", { report_ids: selected, note });
      toast.success(`${data.confirmed_ids.length} laporan dikonfirmasi (${formatRp(data.total)})`);
      setSelected([]); setConfirmOpen(false); setNote("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal konfirmasi"); }
  };

  const doReturn = async () => {
    if (!returnReason.trim()) return toast.error("Alasan wajib diisi");
    try {
      await api.post(`/admin/reports/${returning.id}/return`, { reason: returnReason });
      toast.success("Laporan dikembalikan ke PJ");
      setReturning(null); setReturnReason("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1B4D3E]">Konfirmasi Laporan PJ</h2>
          <p className="text-sm text-[#64748B]">Pilih laporan yang uangnya sudah diterima lalu konfirmasi.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="report-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="diajukan">Diajukan</SelectItem>
              <SelectItem value="dikonfirmasi">Dikonfirmasi</SelectItem>
              <SelectItem value="dikembalikan">Dikembalikan</SelectItem>
              <SelectItem value="draft">Draf</SelectItem>
              <SelectItem value="all">Semua</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => setConfirmOpen(true)} disabled={selected.length === 0} data-testid="bulk-confirm-btn">
            <CheckCircle2 size={14} className="mr-1" /> Konfirmasi ({selected.length})
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-3 px-3"></th>
              <th className="py-3 px-3 text-left">Tanggal</th>
              <th className="py-3 px-3 text-left">Kelas</th>
              <th className="py-3 px-3 text-left">PJ</th>
              <th className="py-3 px-3 text-right">Nominal</th>
              <th className="py-3 px-3 text-left">Status</th>
              <th className="py-3 px-3 text-left">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[#94A3B8]">Tidak ada laporan.</td></tr>}
            {reports.map((r) => (
              <tr key={r.id} className="border-t border-[#F1F5F9]" data-testid={`admin-report-row-${r.id}`}>
                <td className="py-3 px-3">
                  {r.status === "diajukan" && (
                    <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`report-check-${r.id}`} />
                  )}
                </td>
                <td className="py-3 px-3">{formatDateID(r.date)}</td>
                <td className="py-3 px-3">{r.class_name}</td>
                <td className="py-3 px-3">{r.pj_name}</td>
                <td className="py-3 px-3 text-right font-semibold">{formatRp(r.amount)}</td>
                <td className="py-3 px-3"><Badge className={statusColor[r.status]}>{statusLabel[r.status]}</Badge>
                  {r.returned_reason && <div className="text-xs text-red-700 mt-1">Alasan: {r.returned_reason}</div>}
                </td>
                <td className="py-3 px-3">
                  {r.status === "diajukan" && (
                    <Button size="sm" variant="outline" onClick={() => setReturning(r)} data-testid={`return-btn-${r.id}`}><XCircle size={12} className="mr-1" /> Kembalikan</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected.length > 0 && (
        <div className="mt-4 p-3 rounded-lg bg-[#FBF5E8] text-[#1B4D3E] font-semibold" data-testid="confirmation-total">
          Total terpilih: {formatRp(selectedTotal)}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Konfirmasi Penyerahan Uang</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">
              {selected.length} laporan senilai <strong>{formatRp(selectedTotal)}</strong> akan dikonfirmasi.
              Pastikan jumlah uang yang Anda terima <strong>sama persis</strong>.
            </p>
            <div>
              <Label>Catatan (opsional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="confirm-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={confirmSelected} data-testid="confirm-submit-btn">Konfirmasi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!returning} onOpenChange={(o) => !o && setReturning(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kembalikan Laporan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">Alasan akan dilihat PJ supaya dapat memperbaiki laporan.</p>
            <Textarea value={returnReason} onChange={(e) => setReturnReason(e.target.value)} rows={3} data-testid="return-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturning(null)}>Batal</Button>
            <Button onClick={doReturn} data-testid="return-submit-btn">Kembalikan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AlumniPledgeTab({ items, reload }) {
  const [confirming, setConfirming] = useState(null);
  const [method, setMethod] = useState("transfer");
  const [date, setDate] = useState(todayISO());

  const confirm = async () => {
    try {
      await api.post(`/admin/alumni-pledges/${confirming.id}/confirm`, { method, date, note: "" });
      toast.success(`Pledge Rp${confirming.amount.toLocaleString("id-ID")} dikonfirmasi menjadi penerimaan`);
      setConfirming(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const reject = async (p) => {
    const r = window.prompt("Alasan penolakan:");
    if (!r) return;
    try {
      await api.post(`/admin/alumni-pledges/${p.id}/reject`, { reason: r });
      toast.success("Pledge ditolak"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const pending = items.filter((p) => p.status === "pending");
  const other = items.filter((p) => p.status !== "pending");

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-[#1B4D3E]"><HeartHandshake size={18} className="inline mr-1" /> Pledge Alumni</h2>
          <p className="text-xs text-[#64748B]">Niat kontribusi dari halaman publik <code>/infaq/alumni</code>. Konfirmasi setelah uang diterima.</p>
        </div>
        <Button variant="outline" onClick={() => {
          navigator.clipboard.writeText(`${window.location.origin}/infaq/alumni`);
          toast.success("Tautan halaman alumni tersalin");
        }} data-testid="copy-alumni-link-btn">Salin Tautan Halaman</Button>
      </div>

      <h3 className="font-semibold text-[#1B4D3E] mb-2">Menunggu Konfirmasi ({pending.length})</h3>
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-2 px-3 text-left">Waktu</th>
              <th className="py-2 px-3 text-left">Nama</th>
              <th className="py-2 px-3 text-left">Kontak</th>
              <th className="py-2 px-3 text-right">Nominal</th>
              <th className="py-2 px-3 text-left">Pesan</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[#94A3B8]">Belum ada pledge menunggu.</td></tr>}
            {pending.map((p) => (
              <tr key={p.id} className="border-t border-[#F1F5F9]" data-testid={`pledge-row-${p.id}`}>
                <td className="py-3 px-3 text-xs">{formatDateTimeID(p.submitted_at)}</td>
                <td className="py-3 px-3">{p.name || <em className="text-[#94A3B8]">Anonim</em>}</td>
                <td className="py-3 px-3 text-[#64748B]">{p.contact || "-"}</td>
                <td className="py-3 px-3 text-right font-semibold text-[#C59B27]">{formatRp(p.amount)}</td>
                <td className="py-3 px-3 text-[#64748B] text-xs max-w-xs truncate" title={p.note}>{p.note || "-"}</td>
                <td className="py-3 px-3 flex gap-2">
                  <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => setConfirming(p)} data-testid={`confirm-pledge-${p.id}`}>Konfirmasi</Button>
                  <Button size="sm" variant="outline" onClick={() => reject(p)} data-testid={`reject-pledge-${p.id}`}>Tolak</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="font-semibold text-[#1B4D3E] mb-2">Riwayat ({other.length})</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-2 px-3 text-left">Waktu</th>
              <th className="py-2 px-3 text-left">Nama</th>
              <th className="py-2 px-3 text-right">Nominal</th>
              <th className="py-2 px-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {other.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[#94A3B8]">Belum ada riwayat.</td></tr>}
            {other.map((p) => (
              <tr key={p.id} className="border-t border-[#F1F5F9]">
                <td className="py-2 px-3 text-xs">{formatDateTimeID(p.submitted_at)}</td>
                <td className="py-2 px-3">{p.name || "-"}</td>
                <td className="py-2 px-3 text-right">{formatRp(p.amount)}</td>
                <td className="py-2 px-3">
                  <Badge className={p.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                    {p.status === "confirmed" ? "Terkonfirmasi" : "Ditolak"}
                  </Badge>
                  {p.reject_reason && <div className="text-xs text-red-700 mt-1">{p.reject_reason}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Konfirmasi Pledge</DialogTitle></DialogHeader>
          {confirming && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-[#FBF5E8]">
                <div className="text-xs text-[#64748B]">Dari {confirming.name || "Anonim"}</div>
                <div className="text-2xl font-bold text-[#C59B27]">{formatRp(confirming.amount)}</div>
              </div>
              <div><Label>Tanggal uang diterima</Label><Input type="date" max={todayISO()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="pledge-confirm-date" /></div>
              <div><Label>Metode</Label><Input value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pledge-confirm-method" /></div>
              <p className="text-xs text-[#64748B]">Pledge akan dicatat sebagai Penerimaan Langsung (sumber: alumni) dan masuk saldo.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={confirm} data-testid="pledge-confirm-submit">Konfirmasi & Catat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DirectReceiptTab({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), amount: "", source: "umum", method: "tunai", donor_name_internal: "", note: "" });

  const submit = async () => {
    try {
      await api.post("/admin/direct-receipts", { ...form, amount: parseInt(form.amount) });
      toast.success("Penerimaan dicatat");
      setOpen(false);
      setForm({ date: todayISO(), amount: "", source: "umum", method: "tunai", donor_name_internal: "", note: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const voidItem = async (id) => {
    const reason = window.prompt("Alasan pembatalan:");
    if (!reason) return;
    try {
      await api.post(`/admin/direct-receipts/${id}/void`, { reason });
      toast.success("Transaksi dibatalkan");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#1B4D3E]">Penerimaan Langsung</h2>
        <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => setOpen(true)} data-testid="add-direct-btn"><Plus size={14} className="mr-1" /> Catat</Button>
      </div>
      <p className="text-xs text-[#94A3B8] mb-4">Gunakan untuk pemasukan dari alumni/donatur langsung. Bukan untuk mencatat ulang setoran PJ.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-3 px-3 text-left">Tanggal</th>
              <th className="py-3 px-3 text-left">Sumber</th>
              <th className="py-3 px-3 text-left">Donatur (internal)</th>
              <th className="py-3 px-3 text-right">Nominal</th>
              <th className="py-3 px-3 text-left">Status</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[#94A3B8]">Belum ada data.</td></tr>}
            {items.map((d) => (
              <tr key={d.id} className="border-t border-[#F1F5F9]">
                <td className="py-3 px-3">{formatDateID(d.date)}</td>
                <td className="py-3 px-3"><Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{sourceLabel[d.source]}</Badge></td>
                <td className="py-3 px-3 text-[#64748B]">{d.donor_name_internal || "-"}</td>
                <td className="py-3 px-3 text-right font-semibold">{formatRp(d.amount)}</td>
                <td className="py-3 px-3">{d.is_void ? <Badge className="bg-red-100 text-red-800">Dibatalkan</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Aktif</Badge>}</td>
                <td className="py-3 px-3">{!d.is_void && <Button size="sm" variant="outline" onClick={() => voidItem(d.id)} data-testid={`void-direct-${d.id}`}>Batalkan</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Catat Penerimaan Langsung</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" max={todayISO()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="direct-date" /></div>
            <div><Label>Sumber</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="direct-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(sourceLabel).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nominal (Rp)</Label><Input type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="direct-amount" /></div>
            <div><Label>Metode</Label><Input value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="direct-method" /></div>
            <div className="sm:col-span-2"><Label>Nama donatur (internal, opsional)</Label><Input value={form.donor_name_internal} onChange={(e) => setForm({ ...form, donor_name_internal: e.target.value })} data-testid="direct-donor" /></div>
            <div className="sm:col-span-2"><Label>Catatan</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="direct-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={submit} data-testid="direct-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ExpenseTab({ items, needs, reload, balance }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), description: "", amount: "", category: "ruang_baca", need_id: "", note: "" });
  const [receiptFileId, setReceiptFileId] = useState(null);
  const [publicReceiptFileId, setPublicReceiptFileId] = useState(null);
  const privateRef = useRef(); const publicRef = useRef();

  const upload = async (file, visibility) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) { toast.error("Maks 5MB"); return null; }
    const fd = new FormData(); fd.append("file", file); fd.append("visibility", visibility);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.id;
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); return null; }
  };

  const submit = async () => {
    try {
      await api.post("/admin/expenses", {
        date: form.date, description: form.description, amount: parseInt(form.amount),
        category: form.category, need_id: form.need_id || null,
        receipt_file_id: receiptFileId, public_receipt_file_id: publicReceiptFileId, note: form.note,
      });
      toast.success("Pengeluaran dicatat");
      setOpen(false); setReceiptFileId(null); setPublicReceiptFileId(null);
      setForm({ date: todayISO(), description: "", amount: "", category: "ruang_baca", need_id: "", note: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const voidItem = async (id) => {
    const reason = window.prompt("Alasan pembatalan:");
    if (!reason) return;
    try { await api.post(`/admin/expenses/${id}/void`, { reason }); toast.success("Dibatalkan"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const downloadReceipt = async (id) => {
    try {
      const res = await api.get(`/files/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error("Gagal memuat berkas"); }
  };

  const needsMap = Object.fromEntries(needs.map((n) => [n.id, n.name]));

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#1B4D3E]">Pengeluaran</h2>
          <p className="text-xs text-[#94A3B8]">Saldo tersedia: {formatRp(balance)}</p>
        </div>
        <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => setOpen(true)} data-testid="add-expense-btn"><Plus size={14} className="mr-1" /> Catat</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-3 px-3 text-left">Tanggal</th>
              <th className="py-3 px-3 text-left">Deskripsi</th>
              <th className="py-3 px-3 text-left">Kategori</th>
              <th className="py-3 px-3 text-left">Kebutuhan</th>
              <th className="py-3 px-3 text-right">Nominal</th>
              <th className="py-3 px-3 text-left">Bukti</th>
              <th className="py-3 px-3 text-left">Status</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-[#94A3B8]">Belum ada data.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} className="border-t border-[#F1F5F9]">
                <td className="py-3 px-3">{formatDateID(e.date)}</td>
                <td className="py-3 px-3">{e.description}</td>
                <td className="py-3 px-3"><Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{categoryLabel[e.category]}</Badge></td>
                <td className="py-3 px-3 text-[#64748B]">{needsMap[e.need_id] || "-"}</td>
                <td className="py-3 px-3 text-right font-semibold text-red-700">{formatRp(e.amount)}</td>
                <td className="py-3 px-3 flex gap-2">
                  {e.receipt_file_id && <Button size="sm" variant="outline" onClick={() => downloadReceipt(e.receipt_file_id)} data-testid={`view-receipt-${e.id}`}>Nota</Button>}
                  {e.public_receipt_file_id && <a href={publicFileUrl(e.public_receipt_file_id)} target="_blank" rel="noreferrer" className="text-xs underline text-[#1B4D3E]">Publik</a>}
                </td>
                <td className="py-3 px-3">{e.is_void ? <Badge className="bg-red-100 text-red-800">Dibatalkan</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Aktif</Badge>}</td>
                <td className="py-3 px-3">{!e.is_void && <Button size="sm" variant="outline" onClick={() => voidItem(e.id)} data-testid={`void-exp-${e.id}`}>Batalkan</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Catat Pengeluaran</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" max={todayISO()} value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })} data-testid="exp-date" /></div>
            <div><Label>Nominal (Rp)</Label><Input type="number" min="1" value={form.amount} onChange={(ev) => setForm({ ...form, amount: ev.target.value })} data-testid="exp-amount" /></div>
            <div className="sm:col-span-2"><Label>Deskripsi</Label><Input value={form.description} onChange={(ev) => setForm({ ...form, description: ev.target.value })} data-testid="exp-description" placeholder="Contoh: Pembelian 4 rak buku" /></div>
            <div><Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ruang_baca">Ruang baca</SelectItem>
                  <SelectItem value="kebun">Kebun mini</SelectItem>
                  <SelectItem value="pendukung">Biaya pendukung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Kebutuhan terkait</Label>
              <Select value={form.need_id || "none"} onValueChange={(v) => setForm({ ...form, need_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="exp-need"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(tidak terkait)</SelectItem>
                  {needs.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Nota asli (privat, PDF/gambar, maks 5MB)</Label>
              <input ref={privateRef} type="file" accept="image/*,application/pdf"
                onChange={async (ev) => { const id = await upload(ev.target.files[0], "private"); if (id) { setReceiptFileId(id); toast.success("Nota privat terunggah"); } }}
                data-testid="exp-receipt-private" />
              {receiptFileId && <div className="text-xs text-emerald-700 mt-1">Terunggah ✓</div>}
            </div>
            <div className="sm:col-span-2"><Label>Nota untuk publik (sudah disamarkan, opsional)</Label>
              <input ref={publicRef} type="file" accept="image/*,application/pdf"
                onChange={async (ev) => { const id = await upload(ev.target.files[0], "public"); if (id) { setPublicReceiptFileId(id); toast.success("Nota publik terunggah"); } }}
                data-testid="exp-receipt-public" />
              {publicReceiptFileId && <div className="text-xs text-emerald-700 mt-1">Terunggah ✓</div>}
            </div>
            <div className="sm:col-span-2"><Label>Catatan internal</Label><Textarea rows={2} value={form.note} onChange={(ev) => setForm({ ...form, note: ev.target.value })} data-testid="exp-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={submit} data-testid="exp-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function NeedsTab({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", quantity: 1, unit: "unit", budget: 0, category: "ruang_baca", status: "direncanakan", detail: [] };
  const [form, setForm] = useState(empty);
  const [reason, setReason] = useState("");

  const openCreate = () => { setEditing(null); setForm(empty); setReason(""); setOpen(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...n, detail: n.detail || [] }); setReason(""); setOpen(true); };

  const submit = async () => {
    try {
      const payload = { ...form, quantity: parseInt(form.quantity), budget: parseInt(form.budget) };
      if (editing) await api.put(`/admin/needs/${editing.id}`, payload, { params: { reason } });
      else await api.post(`/admin/needs`, payload);
      toast.success("Disimpan"); setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    const r = window.prompt("Alasan penghapusan:"); if (!r) return;
    try { await api.delete(`/admin/needs/${id}`, { params: { reason: r } }); toast.success("Dihapus"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#1B4D3E]">Daftar Kebutuhan</h2>
        <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={openCreate} data-testid="add-need-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-3 px-3 text-left">Nama</th>
              <th className="py-3 px-3 text-left">Kategori</th>
              <th className="py-3 px-3 text-right">Jumlah</th>
              <th className="py-3 px-3 text-right">Anggaran</th>
              <th className="py-3 px-3 text-right">Realisasi</th>
              <th className="py-3 px-3 text-left">Status</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.id} className="border-t border-[#F1F5F9]">
                <td className="py-3 px-3 font-medium">{n.name}</td>
                <td className="py-3 px-3"><Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{categoryLabel[n.category]}</Badge></td>
                <td className="py-3 px-3 text-right">{n.quantity} {n.unit}</td>
                <td className="py-3 px-3 text-right">{formatRp(n.budget)}</td>
                <td className="py-3 px-3 text-right text-[#C59B27]">{formatRp(n.realized || 0)}</td>
                <td className="py-3 px-3">{n.status}</td>
                <td className="py-3 px-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(n)} data-testid={`edit-need-${n.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => del(n.id)} data-testid={`del-need-${n.id}`}>Hapus</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Kebutuhan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="need-name" /></div>
            <div><Label>Jumlah</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="need-qty" /></div>
            <div><Label>Satuan</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="need-unit" /></div>
            <div><Label>Anggaran (Rp)</Label><Input type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="need-budget" /></div>
            <div><Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="need-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ruang_baca">Ruang baca</SelectItem>
                  <SelectItem value="kebun">Kebun mini</SelectItem>
                  <SelectItem value="cadangan">Cadangan</SelectItem>
                  <SelectItem value="pendukung">Pendukung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="need-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="direncanakan">Direncanakan</SelectItem>
                  <SelectItem value="dipesan">Dipesan</SelectItem>
                  <SelectItem value="sebagian">Sebagian diterima</SelectItem>
                  <SelectItem value="selesai">Selesai</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editing && <div className="sm:col-span-2"><Label>Alasan perubahan</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="need-reason" /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={submit} data-testid="need-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ClassesTab({ classes, users, reload }) {
  const [classOpen, setClassOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [cForm, setCForm] = useState({ id: null, name: "", pj_user_id: "" });
  const [uForm, setUForm] = useState({ email: "", name: "", password: "", role: "pj", class_ids: [] });

  const pjUsers = users.filter((u) => u.role === "pj");

  const saveClass = async () => {
    try {
      if (cForm.id) await api.put(`/admin/classes/${cForm.id}`, { name: cForm.name, pj_user_id: cForm.pj_user_id || null });
      else await api.post("/admin/classes", { name: cForm.name, pj_user_id: cForm.pj_user_id || null });
      toast.success("Disimpan"); setClassOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };
  const delClass = async (id) => {
    if (!window.confirm("Hapus kelas?")) return;
    try { await api.delete(`/admin/classes/${id}`); toast.success("Terhapus"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };
  const saveUser = async () => {
    try {
      await api.post("/admin/users", uForm);
      toast.success("Akun dibuat"); setUserOpen(false);
      setUForm({ email: "", name: "", password: "", role: "pj", class_ids: [] });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };
  const resetPw = async (uid) => {
    const pw = window.prompt("Kata sandi baru (min. 8 karakter):");
    if (!pw) return;
    try { await api.put(`/admin/users/${uid}`, { new_password: pw }); toast.success("Kata sandi direset"); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active }); toast.success("Diperbarui"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1B4D3E]"><BookOpen size={16} className="inline mr-1" /> Daftar Kelas</h2>
          <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => { setCForm({ id: null, name: "", pj_user_id: "" }); setClassOpen(true); }} data-testid="add-class-btn"><Plus size={12} className="mr-1" /> Tambah</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]"><tr><th className="py-2 px-2 text-left">Nama</th><th className="py-2 px-2 text-left">PJ</th><th></th></tr></thead>
          <tbody>
            {classes.map((c) => (
              <tr key={c.id} className="border-t border-[#F1F5F9]">
                <td className="py-2 px-2">{c.name}</td>
                <td className="py-2 px-2 text-[#64748B]">{c.pj?.name || "-"}</td>
                <td className="py-2 px-2 flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => { setCForm({ id: c.id, name: c.name, pj_user_id: c.pj_user_id || "" }); setClassOpen(true); }} data-testid={`edit-class-${c.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => delClass(c.id)} data-testid={`del-class-${c.id}`}>Hapus</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-5 border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1B4D3E]"><Users size={16} className="inline mr-1" /> Akun PJ</h2>
          <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => setUserOpen(true)} data-testid="add-user-btn"><Plus size={12} className="mr-1" /> Tambah</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]"><tr><th className="py-2 px-2 text-left">Nama</th><th className="py-2 px-2 text-left">Email</th><th className="py-2 px-2">Peran</th><th className="py-2 px-2">Status</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#F1F5F9]">
                <td className="py-2 px-2">{u.name}</td>
                <td className="py-2 px-2 text-[#64748B] text-xs">{u.email}</td>
                <td className="py-2 px-2 text-center"><Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{u.role}</Badge></td>
                <td className="py-2 px-2 text-center">{u.is_active ? "Aktif" : "Nonaktif"}</td>
                <td className="py-2 px-2 flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => resetPw(u.id)} data-testid={`reset-pw-${u.id}`}>Reset Sandi</Button>
                  {u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>{u.is_active ? "Nonaktifkan" : "Aktifkan"}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={classOpen} onOpenChange={setClassOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{cForm.id ? "Edit" : "Tambah"} Kelas</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama Kelas</Label><Input value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} data-testid="class-name" /></div>
            <div><Label>Penanggung Jawab (PJ)</Label>
              <Select value={cForm.pj_user_id || "none"} onValueChange={(v) => setCForm({ ...cForm, pj_user_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="class-pj"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(belum ditugaskan)</SelectItem>
                  {pjUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClassOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={saveClass} data-testid="class-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Akun PJ</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label>Kata sandi awal (min 8 karakter)</Label><Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-pw" /></div>
            <div><Label>Peran</Label>
              <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pj">PJ Kelas</SelectItem>
                  <SelectItem value="admin">Bendahara/Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Kelas yang ditugaskan</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={uForm.class_ids.includes(c.id)}
                      onCheckedChange={(v) => setUForm({ ...uForm, class_ids: v ? [...uForm.class_ids, c.id] : uForm.class_ids.filter((x) => x !== c.id) })}
                      data-testid={`user-class-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <p className="text-xs text-[#94A3B8]">PJ akan diminta mengganti kata sandi saat login pertama.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={saveUser} data-testid="user-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PostsTab({ items, reload }) {
  const [open, setOpen] = useState(false);
  const empty = { title: "", date: todayISO(), description: "", category: "ruang_baca", status: "draft", is_plan: false, photo_file_ids: [] };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Maks 5MB");
    const fd = new FormData(); fd.append("file", file); fd.append("visibility", "public");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_file_ids: [...f.photo_file_ids, data.id] }));
      toast.success("Foto terunggah");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const submit = async () => {
    try {
      if (editing) await api.put(`/admin/progress-posts/${editing.id}`, form);
      else await api.post(`/admin/progress-posts`, form);
      toast.success("Disimpan"); setOpen(false); setForm(empty); setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus posting?")) return;
    try { await api.delete(`/admin/progress-posts/${id}`); toast.success("Dihapus"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const openEdit = (p) => { setEditing(p); setForm({ ...p, photo_file_ids: p.photo_file_ids || [] }); setOpen(true); };
  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#1B4D3E]"><ImageIcon size={18} className="inline mr-1" /> Perkembangan</h2>
        <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={openCreate} data-testid="add-post-btn"><Plus size={14} className="mr-1" /> Tulis Posting</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-4 border-[#E2E8F0]">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>{p.status === "published" ? "Terbit" : "Draf"}</Badge>
              <Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{categoryLabel[p.category]}</Badge>
              {p.is_plan && <Badge className="bg-amber-100 text-amber-800">Rencana penataan</Badge>}
              <span className="text-xs text-[#94A3B8]">{formatDateID(p.date)}</span>
            </div>
            <h3 className="font-semibold">{p.title}</h3>
            <p className="text-sm text-[#64748B] mt-1">{p.description}</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`}>Edit</Button>
              <Button size="sm" variant="outline" onClick={() => del(p.id)} data-testid={`del-post-${p.id}`}>Hapus</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tulis"} Posting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="post-date" /></div>
              <div><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ruang_baca">Ruang baca</SelectItem><SelectItem value="kebun">Kebun mini</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deskripsi</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="post-desc" /></div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.is_plan} onCheckedChange={(v) => setForm({ ...form, is_plan: !!v })} id="is-plan" data-testid="post-is-plan" />
              <Label htmlFor="is-plan" className="text-sm">Foto rancangan (akan diberi label "Rencana penataan")</Label>
            </div>
            <div><Label>Foto (publik, maks 5MB)</Label>
              <input type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} data-testid="post-photo-input" />
              {form.photo_file_ids.length > 0 && <div className="text-xs text-emerald-700 mt-1">{form.photo_file_ids.length} foto terunggah</div>}
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="post-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draf</SelectItem><SelectItem value="published">Terbit</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={submit} data-testid="post-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SettingsTab({ settings, reload }) {
  const [form, setForm] = useState(settings || {});
  const [newHoliday, setNewHoliday] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  useEffect(() => { setForm(settings || {}); }, [settings]);

  const save = async () => {
    try {
      const payload = {
        name: form.name, tagline: form.tagline, target_amount: parseInt(form.target_amount) || 0,
        start_date: form.start_date || null, school_days: parseInt(form.school_days) || 30,
        holidays: form.holidays || [], pj_info: form.pj_info || "", how_to_contribute: form.how_to_contribute || "",
        logo_file_id: form.logo_file_id || null,
        admin_notification_email: form.admin_notification_email || "",
        qris_file_id: form.qris_file_id || null,
        qris_merchant_name: form.qris_merchant_name || "",
        payment_instructions: form.payment_instructions || "",
        bank_info: form.bank_info || "",
        pic_name: form.pic_name || "",
        pic_whatsapp: form.pic_whatsapp || "",
      };
      await api.put("/admin/settings", payload);
      toast.success("Pengaturan disimpan"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Maks 5MB");
    const fd = new FormData(); fd.append("file", file); fd.append("visibility", "public");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_file_id: data.id }));
      toast.success("Logo terunggah. Klik Simpan untuk menerapkan.");
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const addHoliday = () => {
    if (!newHoliday) return;
    setForm((f) => ({ ...f, holidays: [...(f.holidays || []), newHoliday] }));
    setNewHoliday("");
  };
  const removeHoliday = (h) => setForm((f) => ({ ...f, holidays: (f.holidays || []).filter((x) => x !== h) }));

  return (
    <Card className="p-5 border-[#E2E8F0]">
      <h2 className="text-xl font-bold text-[#1B4D3E] mb-4"><SettingsIcon size={18} className="inline mr-1" /> Pengaturan Gerakan</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label>Nama Gerakan</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="set-name" /></div>
        <div className="sm:col-span-2"><Label>Tagline</Label><Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="set-tagline" /></div>
        <div><Label>Target (Rp)</Label><Input type="number" value={form.target_amount || 0} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} data-testid="set-target" /></div>
        <div><Label>Jumlah Hari Sekolah</Label><Input type="number" value={form.school_days || 30} onChange={(e) => setForm({ ...form, school_days: e.target.value })} data-testid="set-days" /></div>
        <div><Label>Tanggal Mulai</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="set-start" /></div>
        <div className="sm:col-span-2">
          <Label>Hari Libur Tambahan</Label>
          <div className="flex gap-2 mt-2">
            <Input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} data-testid="set-holiday-input" />
            <Button variant="outline" onClick={addHoliday} data-testid="set-holiday-add">Tambah</Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(form.holidays || []).map((h) => (
              <Badge key={h} className="bg-amber-100 text-amber-900 cursor-pointer" onClick={() => removeHoliday(h)}>{formatDateID(h)} ×</Badge>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2"><Label>Petunjuk Penitipan Infak</Label><Textarea rows={3} value={form.how_to_contribute || ""} onChange={(e) => setForm({ ...form, how_to_contribute: e.target.value })} data-testid="set-howto" placeholder="Mis: Transfer BSI 1234567890 a.n. Yayasan Khairul Falah / tunai ke bendahara, dll." /></div>
        <div className="sm:col-span-2"><Label>Penanggung Jawab (teks publik)</Label><Textarea rows={2} value={form.pj_info || ""} onChange={(e) => setForm({ ...form, pj_info: e.target.value })} data-testid="set-pjinfo" /></div>
        <div className="sm:col-span-2">
          <Label>Email Notifikasi (menerima laporan mingguan setiap Jumat sore)</Label>
          <Input type="email" value={form.admin_notification_email || ""}
            onChange={(e) => setForm({ ...form, admin_notification_email: e.target.value })}
            placeholder="bendahara@contoh.com" data-testid="set-notif-email" />
          <p className="text-xs text-[#94A3B8] mt-1">Jika kosong, email mingguan tidak dikirim.</p>
        </div>
        <div className="sm:col-span-2">
          <Label>Logo (publik, maks 5MB)</Label>
          <div className="flex items-center gap-3 mt-2">
            {form.logo_file_id && <img src={publicFileUrl(form.logo_file_id)} alt="Logo" className="h-16 w-16 rounded-full object-cover border border-[#C59B27]/40" />}
            <input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="set-logo-input" />
          </div>
        </div>
      </div>

      <PaymentSettingsSection form={form} setForm={setForm} />

      <div className="mt-6">
        <Button className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={save} data-testid="set-save-btn">Simpan Pengaturan</Button>
      </div>

      <AlumniQrSection />
    </Card>
  );
}

function PaymentSettingsSection({ form, setForm }) {
  const uploadQris = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      return toast.error("QRIS harus JPG/PNG/WebP");
    }
    if (file.size > 5 * 1024 * 1024) return toast.error("Maks 5MB");
    const fd = new FormData(); fd.append("file", file); fd.append("visibility", "public");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, qris_file_id: data.id }));
      toast.success("QRIS terunggah. Klik Simpan untuk menerapkan.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); }
  };

  const removeQris = () => {
    if (!window.confirm("Hapus QRIS tersimpan?")) return;
    setForm((f) => ({ ...f, qris_file_id: null }));
    toast.info("QRIS akan dihapus setelah klik Simpan");
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#E2E8F0]" data-testid="payment-settings-section">
      <h3 className="text-lg font-bold text-[#1B4D3E] mb-1">Pembayaran & Konfirmasi WhatsApp</h3>
      <p className="text-sm text-[#64748B] mb-4">Isi informasi pembayaran yang ditampilkan di halaman publik.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label>Informasi Rekening Bank</Label>
          <Textarea rows={2} value={form.bank_info || ""} onChange={(e) => setForm({ ...form, bank_info: e.target.value })}
            placeholder="Mis: BSI 1234567890 a.n. Yayasan Khairul Falah" data-testid="set-bank-info" />
        </div>
        <div className="sm:col-span-2">
          <Label>Petunjuk Pembayaran (opsional)</Label>
          <Textarea rows={2} value={form.payment_instructions || ""} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })}
            placeholder="Mis: Transfer kapan saja, kirim bukti via WhatsApp ke pengurus." data-testid="set-payment-instructions" />
        </div>
        <div className="sm:col-span-2">
          <Label>Gambar QRIS (JPG/PNG/WebP, maks 5MB)</Label>
          <div className="flex items-start gap-4 mt-2">
            {form.qris_file_id && (
              <div className="shrink-0">
                <img src={publicFileUrl(form.qris_file_id)} alt="QRIS" className="h-40 w-40 object-contain border border-[#C59B27]/40 rounded-lg bg-white p-1" data-testid="qris-preview" />
              </div>
            )}
            <div className="flex-1 space-y-2">
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadQris(e.target.files[0])} data-testid="set-qris-input" />
              {form.qris_file_id && (
                <Button type="button" size="sm" variant="outline" onClick={removeQris} data-testid="set-qris-remove">
                  Hapus QRIS
                </Button>
              )}
              <p className="text-xs text-[#94A3B8]">QRIS asli dipertahankan agar tetap terbaca saat dipindai.</p>
            </div>
          </div>
        </div>
        <div>
          <Label>Nama Penerima/Merchant QRIS</Label>
          <Input value={form.qris_merchant_name || ""} onChange={(e) => setForm({ ...form, qris_merchant_name: e.target.value })}
            placeholder="Mis: Yayasan Khairul Falah" data-testid="set-qris-merchant" />
        </div>
        <div>
          <Label>Nama Pengurus Penerima Konfirmasi</Label>
          <Input value={form.pic_name || ""} onChange={(e) => setForm({ ...form, pic_name: e.target.value })}
            placeholder="Mis: Ibu Siti (Bendahara)" data-testid="set-pic-name" />
        </div>
        <div className="sm:col-span-2">
          <Label>Nomor WhatsApp Pengurus</Label>
          <Input value={form.pic_whatsapp || ""} onChange={(e) => setForm({ ...form, pic_whatsapp: e.target.value })}
            placeholder="Contoh: 08123456789, 628123456789, atau +628123456789" data-testid="set-pic-whatsapp" />
          <p className="text-xs text-[#94A3B8] mt-1">Format fleksibel; sistem menormalkan ke 62… untuk tautan WhatsApp.</p>
        </div>
      </div>
    </div>
  );
}

function AlumniQrSection() {
  const [downloading, setDownloading] = useState(false);
  const qrUrl = `${API}/public/qr?path=/infaq/alumni&_t=${Date.now()}`;
  const alumniLink = `${window.location.origin}/infaq/alumni`;

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "qr-alumni-infak-khairul-falah.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("QR Code tersimpan");
    } catch (e) { toast.error("Gagal mengunduh QR"); }
    finally { setDownloading(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(alumniLink);
      toast.success("Tautan tersalin");
    } catch (e) { toast.error("Gagal menyalin"); }
  };

  const printPoster = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Pop-up diblokir browser");
    w.document.write(`
      <!DOCTYPE html><html lang="id"><head><meta charset="utf-8"><title>QR Infak Alumni</title>
      <style>
        @page { size: A5; margin: 20mm; }
        body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; text-align: center; color: #1B4D3E; margin: 0; padding: 24px; }
        .brand { font-size: 18px; letter-spacing: 2px; color: #C59B27; }
        h1 { font-size: 28px; margin: 8px 0 4px; }
        .tagline { color: #64748B; font-size: 13px; margin-bottom: 24px; }
        img { width: 280px; height: 280px; margin: 16px 0; }
        .url { font-family: monospace; font-size: 11px; color: #64748B; word-break: break-all; margin-top: 8px; }
        .cta { margin-top: 20px; font-size: 14px; color: #1B4D3E; }
        .border { border: 3px solid #C59B27; border-radius: 12px; padding: 24px; display: inline-block; max-width: 420px; }
      </style></head><body>
      <div class="border">
        <div class="brand">INFAK KHAIRUL FALAH</div>
        <h1>Dukung Ruang Baca & Kebun Belajar</h1>
        <div class="tagline">Madrasah Khairul Falah &middot; Transparan &amp; Sukarela</div>
        <img src="${qrUrl}" alt="QR Code"/>
        <div class="cta"><b>Pindai untuk berinfak</b></div>
        <div class="url">${alumniLink}</div>
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 500);</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#E2E8F0]" data-testid="qr-section">
      <h3 className="text-lg font-bold text-[#1B4D3E] flex items-center gap-2 mb-1">
        <QrCode size={18} /> QR Code Alumni & Donatur
      </h3>
      <p className="text-sm text-[#64748B] mb-4">
        Pindai dengan kamera HP untuk membuka halaman kontribusi alumni. Cetak di brosur, papan madrasah, atau selebaran.
      </p>
      <div className="flex flex-col sm:flex-row items-start gap-5">
        <div className="p-3 bg-white border-2 border-[#C59B27]/40 rounded-lg">
          <img
            src={qrUrl}
            alt="QR Code Alumni"
            className="w-44 h-44 sm:w-56 sm:h-56 block"
            data-testid="qr-image"
          />
        </div>
        <div className="flex-1 space-y-3">
          <div className="p-3 rounded-lg bg-[#EAF2EF]">
            <div className="text-xs text-[#64748B] mb-1">Tautan yang di-encode:</div>
            <div className="font-mono text-xs text-[#1B4D3E] break-all" data-testid="qr-url">{alumniLink}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={download} disabled={downloading} className="bg-[#1B4D3E] hover:bg-[#143E32]" data-testid="qr-download-btn">
              <Download size={14} className="mr-2" /> {downloading ? "Mengunduh..." : "Unduh PNG"}
            </Button>
            <Button onClick={printPoster} variant="outline" data-testid="qr-print-btn">
              <FileDown size={14} className="mr-2" /> Cetak Poster A5
            </Button>
            <Button onClick={copyLink} variant="outline" data-testid="qr-copy-link-btn">
              Salin Tautan
            </Button>
          </div>
          <p className="text-xs text-[#94A3B8]">Tip: Cetak poster di kertas tebal, tempel di dekat papan pengumuman, atau sertakan di buku rapor.</p>
        </div>
      </div>
    </div>
  );
}

function AuditTab({ items, reload }) {
  return (
    <Card className="p-5 border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#1B4D3E]"><History size={18} className="inline mr-1" /> Audit Log</h2>
        <Button variant="outline" onClick={reload} data-testid="audit-refresh-btn">Muat ulang</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
            <tr>
              <th className="py-2 px-2 text-left">Waktu</th>
              <th className="py-2 px-2 text-left">Pengguna</th>
              <th className="py-2 px-2 text-left">Aksi</th>
              <th className="py-2 px-2 text-left">Entitas</th>
              <th className="py-2 px-2 text-left">Alasan/Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[#94A3B8]">Belum ada log.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-[#F1F5F9]">
                <td className="py-2 px-2 whitespace-nowrap text-xs">{formatDateTimeID(a.created_at)}</td>
                <td className="py-2 px-2">{a.user_name} <span className="text-xs text-[#94A3B8]">({a.user_role})</span></td>
                <td className="py-2 px-2"><Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{a.action}</Badge></td>
                <td className="py-2 px-2 text-xs">{a.entity} / {a.entity_id?.substring(0, 8)}</td>
                <td className="py-2 px-2 text-xs text-[#64748B]">{a.reason || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
